import { observable, configure, action } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';

configure({ enforceActions: 'always' });

// Online prescription orders: owner-side placeOrder (visitor token) and the
// staff inbox (list / status / escalate). Spec:
// docs/superpowers/specs/2026-09-23-online-prescription-orders-design.md
class PrescriptionOrderStore extends LocalModel {
    constructor() {
        super('prescriptionOrder', {
            namePlural: 'prescriptionOrders',
            namePluralReal: 'prescriptionorders',
            sort: '-createdDate',
            limit: 100,
            api: {
                search: {
                    url: '/api/prescriptionorders/',
                    params: {
                        limit: 100,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/prescriptionorders/',
                    params: {},
                },
                save: {
                    url: '/api/prescriptionorders/',
                    params: {},
                },
                delete: {
                    url: '/api/prescriptionorders/',
                    params: {},
                },
            },
        });
    }

    @observable prescriptionOrder = {};

    @observable prescriptionOrders = [];

    @observable placeOrderStatus = null;

    @observable placeOrderErrors = [];

    @observable visitorOrder = null;

    @action
    async placeOrder({
        customer, animal, weight, request, packageType, pharmacy, comment, birth, jwtToken,
    }) {
        const response = await util.fetchApi('/api/visitors/prescription-order', {
            method: 'POST',
            jwtToken: jwtToken || 'null',
        }, {
            customer, animal, weight, request, packageType, pharmacy, comment, birth,
        });
        this.updateKeyValue('placeOrderStatus', response?.status);
        this.updateKeyValue('placeOrderErrors', Array.isArray(response?.errors) ? response.errors : []);
        if (response?.status === 201) {
            const order = response.data?.order || {};
            return {
                ok: true,
                id: order.id,
                errors: [],
                paymentStatus: order.paymentStatus,
                paymentAmount: order.paymentAmount,
            };
        }
        return { ok: false, id: null, errors: Array.isArray(response?.errors) ? response.errors : [] };
    }

    @action
    async loadVisitorOrder(id, jwtToken) {
        const response = await util.fetchApi(`/api/visitors/prescription-order/${id}`, {
            method: 'GET',
            jwtToken: jwtToken || 'null',
        }, {});
        const order = response?.status === 200 && response.data?.id ? response.data : null;
        this.updateKeyValue('visitorOrder', order);
        return order;
    }

    // What the public order page shows the logged-in owner: the animals, each with the birth
    // date on file for its own owner record (ownerBirth).
    // null = no valid visitor token. Never creates an owner record.
    async loadOwnerContext(customerId, jwtToken) {
        const response = await util.fetchApi(`/api/visitors/prescription-order/context/${customerId}`, {
            method: 'GET',
            jwtToken: jwtToken || 'null',
        }, {});
        if (response?.status !== 200 || !response.data) {
            return null;
        }
        return {
            known: response.data.known === true,
            animals: Array.isArray(response.data.animals) ? response.data.animals : [],
        };
    }

    async requestVippsPayment(id, params = {}, jwtToken) {
        return util.fetchApi(`/api/visitors/prescription-order/${id}/vipps`, {
            method: 'POST',
            jwtToken: jwtToken || 'null',
        }, { ...params });
    }

    async getVippsPaymentStatus(id, uuidv4, jwtToken) {
        return util.fetchApi(`/api/visitors/prescription-order/${id}/payment-status/${uuidv4}`, {
            method: 'GET',
            jwtToken: jwtToken || 'null',
        }, {});
    }

    async refund(id) {
        const response = await util.fetchApi(`/api/prescriptionorders/${id}/refund`, { method: 'POST' }, {});
        return { ok: response?.status === 200, message: response?.message, reason: response?.reason };
    }

    @action
    async loadOrders({ status = 'open' } = {}) {
        const response = await util.fetchApi('/api/prescriptionorders/', { method: 'GET' }, { status, limit: 100 });
        const list = Array.isArray(response?.data) ? response.data : [];
        this.updateKeyValue('prescriptionOrders', list);
        return list;
    }

    @action
    async setStatus(id, status, staffComment) {
        const body = { status };
        if (staffComment !== undefined) {
            body.staffComment = staffComment;
        }
        const response = await util.fetchApi(`/api/prescriptionorders/${id}`, { method: 'PATCH' }, body);
        return response?.status === 200;
    }

    @action
    async escalateToSale(id) {
        const response = await util.fetchApi(`/api/prescriptionorders/${id}/escalate-to-sale`, { method: 'POST' }, {});
        if (response?.status === 201) {
            return response.data;
        }
        return null;
    }
}

const prescriptionOrderStore = new PrescriptionOrderStore();
export default prescriptionOrderStore;
